/**
 * Сторис на главной приложения: `GET/POST /admin/stories`,
 * `GET/PUT/DELETE /admin/stories/{id}`, картинки — `POST /admin/stories/media`.
 *
 * На бэкенде этих ручек ещё нет — по просьбе заказчика раздел работает на
 * mock-хранилище в браузере (`@dorixona/api/stories`), за флагом
 * `VITE_FEATURE_STORIES`. Источник выбирает `VITE_STORIES_MOCK`: когда ручки
 * появятся, переключается он, а экраны остаются теми же.
 */
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mediaUrl } from '@dorixona/api'
import {
  createHttpStoriesBackend,
  createMockStoriesStore,
  type StoriesBackend,
  type StoryDto,
  type StoryInput,
} from '@dorixona/api/stories'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { env } from '../../config/env'
import { shrinkImage } from './image'
import { nowMs } from '@dorixona/utils'

export interface StorySlide {
  /** Нет у слайда, который ещё не сохранён. */
  id?: number
  /** Ключ картинки — уходит в запрос. */
  key: string
  /** Готовый адрес картинки для показа. */
  url: string
  /** Секунды на экране. */
  duration: number
  buttonRu: string
  buttonUz: string
  link: string
}

export interface Story {
  id: number
  titleRu: string
  titleUz: string
  coverKey: string
  /** Адрес обложки; пусто — в кружке первый слайд. */
  coverUrl: string
  slides: StorySlide[]
  /** ISO 8601; null — без ограничения. */
  startsAt: string | null
  endsAt: string | null
  sort: number
  active: boolean
  views: number
  updatedAt: string
}

export function toStory(dto: StoryDto): Story {
  return {
    id: dto.id,
    titleRu: dto.title_ru,
    titleUz: dto.title_uz,
    coverKey: dto.cover_key,
    coverUrl: mediaUrl(dto.cover_url, env.apiBaseUrl),
    slides: dto.slides.map((slide) => ({
      id: slide.id,
      key: slide.image_key,
      url: mediaUrl(slide.image_url, env.apiBaseUrl),
      duration: slide.duration_sec,
      buttonRu: slide.button_ru,
      buttonUz: slide.button_uz,
      link: slide.link,
    })),
    startsAt: dto.starts_at,
    endsAt: dto.ends_at,
    sort: dto.sort,
    active: dto.is_active,
    views: dto.views ?? 0,
    updatedAt: dto.updated_at,
  }
}

export function toStoryInput(story: Omit<Story, 'id' | 'views' | 'updatedAt'>): StoryInput {
  return {
    title_ru: story.titleRu,
    title_uz: story.titleUz,
    cover_key: story.coverKey,
    slides: story.slides.map((slide) => ({
      ...(slide.id === undefined ? {} : { id: slide.id }),
      image_key: slide.key,
      duration_sec: slide.duration,
      button_ru: slide.buttonRu,
      button_uz: slide.buttonUz,
      link: slide.link,
    })),
    starts_at: story.startsAt,
    ends_at: story.endsAt,
    sort: story.sort,
    is_active: story.active,
  }
}

/** Откуда берутся сторис: mock в браузере или ручки API. */
export function useStoriesBackend(): StoriesBackend {
  const api = useApi()
  return useMemo(
    () =>
      env.storiesMock
        ? createMockStoriesStore({ storage: window.localStorage, now: nowMs })
        : createHttpStoriesBackend(api),
    [api],
  )
}

export function useStories() {
  const backend = useStoriesBackend()
  return useQuery({
    queryKey: keys.stories,
    queryFn: async () => (await backend.list()).map(toStory),
  })
}

/** Новая сторис или сохранение существующей — целиком, со слайдами. */
export type StoryToSave = Omit<Story, 'views' | 'updatedAt'>

export function useSaveStory() {
  const backend = useStoriesBackend()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (story: StoryToSave) =>
      toStory(
        story.id
          ? await backend.update(story.id, toStoryInput(story))
          : await backend.create(toStoryInput(story)),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.stories }),
  })
}

/**
 * Несколько сторис разом — перестановка на главной. Ждём все записи, чтобы
 * список не перечитался на середине и не показал порядок наполовину.
 */
export function useSaveManyStories() {
  const backend = useStoriesBackend()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (list: Story[]) =>
      Promise.all(list.map((story) => backend.update(story.id, toStoryInput(story)))),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.stories }),
  })
}

export function useDeleteStory() {
  const backend = useStoriesBackend()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => backend.remove(id),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.stories }),
  })
}

/**
 * Загрузить картинку слайда или обложки. Ответ — ключ и адрес; в сторис
 * картинка попадёт, только когда её сохранят. Для mock картинка сначала
 * ужимается: её держит хранилище браузера, а в нём всего несколько мегабайт.
 */
export function useUploadStoryMedia() {
  const backend = useStoriesBackend()
  return useMutation({
    mutationFn: async (file: File) => {
      const media = await backend.upload(env.storiesMock ? await shrinkImage(file) : file)
      return { key: media.key, url: mediaUrl(media.url, env.apiBaseUrl) }
    },
  })
}
